import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gsap } from 'gsap';
import DesktopWindow from '../components/DesktopWindow';
import ProjectsApp from '../components/apps/ProjectsApp';
import BlogApp from '../components/apps/BlogApp';
import ExperienceApp from '../components/apps/ExperienceApp';
import ContactApp from '../components/apps/ContactApp';
import Resume from './Resume';
import AwardsApp from '../components/apps/AwardsApp';
import ResearchApp from '../components/apps/ResearchApp';
import SkillsApp from '../components/apps/SkillsGlobe';
import { ProjectDetailContent } from '../components/apps/ProjectsApp';
import BinocularEyes from '../components/BinocularEyes';
import MobileEnvironment from '../components/os/MobileEnvironment';
import TabletEnvironment from '../components/os/TabletEnvironment';
import bgNew from '../assets/images/bgNew.jpeg';
import { VscFolderLibrary } from 'react-icons/vsc';
import { VscGlobe } from 'react-icons/vsc';
import { TbWriting } from 'react-icons/tb';
import { HiBriefcase } from 'react-icons/hi2';
import { RiMailSendLine } from 'react-icons/ri';
import { PiTrophyBold } from 'react-icons/pi';
import { HiBeaker } from 'react-icons/hi2';
import { GoHome } from 'react-icons/go';
import { HiArrowDownTray } from 'react-icons/hi2';
import { RiLock2Line, RiLockUnlockLine } from 'react-icons/ri';
import '../styles/Desktop.css';

/* ── App definitions ── */
// Core apps (always in dock on desktop/tablet, always shown on mobile)
const CORE_APPS = [
  { id: 'projects', title: 'Projects.app', icon: <VscFolderLibrary />, color: '#E85D34', defaultWidth: 920, defaultHeight: 640 },
  { id: 'blog', title: 'Blog.app', icon: <TbWriting />, color: '#4FC3F7', defaultWidth: 760, defaultHeight: 580 },
  { id: 'experience', title: 'Experience.app', icon: <HiBriefcase />, color: '#81C784', defaultWidth: 840, defaultHeight: 620 },
  { id: 'contact', title: 'Contact.app', icon: <RiMailSendLine />, color: '#CE93D8', defaultWidth: 540, defaultHeight: 640 },
  { id: 'resume', title: 'Resume.app', icon: <HiArrowDownTray />, color: '#4CAF50', defaultWidth: 620, defaultHeight: 500 },
];

// Extra apps (only on desktop grid, collapsed on mobile/tablet)
const EXTRA_APPS = [
  { id: 'awards', title: 'Awards.app', icon: <PiTrophyBold />, color: '#FFD54F', defaultWidth: 820, defaultHeight: 620 },
  { id: 'research', title: 'Research.app', icon: <HiBeaker />, color: '#FF8A65', defaultWidth: 760, defaultHeight: 560 },
  { id: 'skills', title: 'Skills.app', icon: <VscGlobe />, color: '#7E57C2', defaultWidth: 1120, defaultHeight: 860 },
];

const ALL_APPS = [...CORE_APPS, ...EXTRA_APPS];

const APP_CONTENT = {
  projects: <ProjectsApp />,
  blog: <BlogApp />,
  experience: <ExperienceApp />,
  contact: <ContactApp />,
  resume: <Resume />,
  awards: <AwardsApp />,
  research: <ResearchApp />,
  skills: <SkillsApp />,
};

const DOCK_APPS = ALL_APPS.filter((app) => app.id !== 'resume');

/* ── Interactive Desktop ── */
const Desktop = () => {
  const [windows, setWindows] = useState({});
  const [topZ, setTopZ] = useState(100);
  const [isTablet, setIsTablet] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileApp, setMobileApp] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [projectDetailWindows, setProjectDetailWindows] = useState({});
  const [blogRedirecting, setBlogRedirecting] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const dockRefs = useRef({});
  const desktopRef = useRef(null);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const [desktopVisible, setDesktopVisible] = useState(false);
  const openWindowRef = useRef(null);

  // ── MutationObserver: toggle desktopVisible when .in-plane class changes ──
  useEffect(() => {
    const el = desktopRef.current;
    if (!el) return;

    const update = () => {
      const live = !el.classList.contains('in-plane');
      setDesktopVisible(live);
      const header = document.querySelector('.header');
      if (header) {
        header.style.transition = 'opacity 0.3s, visibility 0.3s';
        header.style.opacity = live ? '0' : '1';
        header.style.visibility = live ? 'hidden' : 'visible';
      }
    };

    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    update(); // initial check
    return () => obs.disconnect();
  }, []);

  // ── Hide global Header on mobile/tablet (these envs are fullscreen by
  //    design; the desktop in-plane MutationObserver never runs here).
  useEffect(() => {
    const header = document.querySelector('.header');
    if (!header) return;
    if (isMobile || isTablet) {
      header.style.transition = 'opacity 0.3s, visibility 0.3s';
      header.style.opacity = '0';
      header.style.visibility = 'hidden';
    }
    return () => {
      if (isMobile || isTablet) {
        // Don't restore on cleanup — Desktop only unmounts on route change,
        // and the new page (e.g. /blogs) renders its own Header.
      }
    };
  }, [isMobile, isTablet]);

  // ── Lock scroll when locked ──
  useEffect(() => {
    if (isLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isLocked]);

  const toggleLock = useCallback(() => {
    setIsLocked((prev) => !prev);
  }, []);

  // ── Custom cursor (always rendered, visibility controlled by CSS) ──
  useEffect(() => {
    const cursor = cursorRef.current;
    const dot = cursorDotRef.current;
    if (!cursor || !dot) return;
    const onMove = (e) => {
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
      dot.style.left = e.clientX + 'px';
      dot.style.top = e.clientY + 'px';
    };
    const onDown = () => { cursor.classList.add('clicking'); dot.classList.add('clicking'); };
    const onUp = () => { cursor.classList.remove('clicking'); dot.classList.remove('clicking'); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ── Responsive breakpoints ──
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Dock hover animation ──
  const onDockHover = useCallback((e, entering) => {
    const icon = e.currentTarget.querySelector('.dock-icon');
    if (icon) {
      gsap.to(icon, {
        scale: entering ? 1.25 : 1,
        y: entering ? -8 : 0,
        duration: 0.25,
        ease: entering ? 'back.out(2)' : 'power2.out',
      });
    }
  }, []);

  // ── Open window ──
  const openWindow = useCallback((appId) => {
    if (appId === 'blog') {
      setBlogRedirecting(true);
      setTimeout(() => { window.location.href = '/blogs'; }, 1500);
      return;
    }
    if (isTablet) {
      setWindows((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          next[k] = { ...prev[k], isOpen: k === appId };
        });
        if (!next[appId]) {
          next[appId] = { isOpen: true, isMinimized: false, isMaximized: false, zIndex: topZ + 1 };
        } else {
          next[appId] = { ...next[appId], isOpen: true, isMinimized: false, zIndex: topZ + 1 };
        }
        return next;
      });
    } else {
      setWindows((prev) => {
        const existing = prev[appId];
        if (existing?.isOpen && existing?.isMinimized) {
          return { ...prev, [appId]: { ...existing, isMinimized: false, zIndex: topZ + 1 } };
        }
        if (existing?.isOpen) {
          return { ...prev, [appId]: { ...existing, zIndex: topZ + 1 } };
        }
        return {
          ...prev,
          [appId]: { isOpen: true, isMinimized: false, isMaximized: false, zIndex: topZ + 1 },
        };
      });
    }
    setTopZ((z) => z + 1);
  }, [topZ, isTablet]);

  // Expose globally for Header nav
  openWindowRef.current = openWindow;
  useEffect(() => {
    window.__desktopOpenWindow = (appId) => {
      if (openWindowRef.current) openWindowRef.current(appId);
    };
    return () => { delete window.__desktopOpenWindow; };
  }, []);

  // ── Open project detail in a separate popup window ──
  const openProjectDetail = useCallback((projectId, projectTitle) => {
    const winId = `project-detail-${projectId}`;
    setProjectDetailWindows((prev) => ({
      ...prev,
      [winId]: { projectId, title: projectTitle || 'Project Detail' },
    }));
    setWindows((prev) => ({
      ...prev,
      [winId]: { isOpen: true, isMinimized: false, isMaximized: false, zIndex: topZ + 1 },
    }));
    setTopZ((z) => z + 1);
  }, [topZ]);

  useEffect(() => {
    window.__desktopOpenProjectDetail = openProjectDetail;
    return () => { delete window.__desktopOpenProjectDetail; };
  }, [openProjectDetail]);

  const closeProjectDetailWindow = useCallback((winId) => {
    setWindows((prev) => ({
      ...prev,
      [winId]: { ...prev[winId], isOpen: false },
    }));
    setProjectDetailWindows((prev) => {
      const next = { ...prev };
      delete next[winId];
      return next;
    });
  }, []);

  // ── Home: unlock scroll first, then scroll to very top ──
  const scrollToHome = useCallback(() => {
    setIsLocked(false);
    document.body.style.overflow = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const closeWindow = useCallback((appId) => {
    setWindows((prev) => ({
      ...prev,
      [appId]: { ...prev[appId], isOpen: false, isMinimized: false, isMaximized: false },
    }));
  }, []);

  const minimizeWindow = useCallback((appId) => {
    setWindows((prev) => ({
      ...prev,
      [appId]: { ...prev[appId], isMinimized: true },
    }));
  }, []);

  const maximizeWindow = useCallback((appId) => {
    setWindows((prev) => ({
      ...prev,
      [appId]: { ...prev[appId], isMaximized: !prev[appId]?.isMaximized },
    }));
  }, []);

  const focusWindow = useCallback((appId) => {
    setWindows((prev) => ({
      ...prev,
      [appId]: { ...prev[appId], zIndex: topZ + 1 },
    }));
    setTopZ((z) => z + 1);
  }, [topZ]);

  // ── Mobile fullscreen ──
  const openMobileApp = useCallback((appId) => {
    if (appId === 'blog') {
      setBlogRedirecting(true);
      setTimeout(() => { window.location.href = '/blogs'; }, 1500);
      return;
    }
    setMobileApp(appId);
  }, []);
  const closeMobileApp = useCallback(() => setMobileApp(null), []);

  // ── Mobile environment (iOS-style) ──
  if (isMobile) {
    // Pick the most recent project detail (mobile shows one at a time)
    const detailEntries = Object.entries(projectDetailWindows);
    const activeDetail = detailEntries.length ? detailEntries[detailEntries.length - 1][1].projectId : null;
    const closeMobileProjectDetail = () => {
      detailEntries.forEach(([winId]) => closeProjectDetailWindow(winId));
    };
    return (
      <MobileEnvironment
        coreApps={CORE_APPS}
        extraApps={EXTRA_APPS}
        appContent={APP_CONTENT}
        activeApp={mobileApp}
        openApp={openMobileApp}
        closeApp={closeMobileApp}
        bg={bgNew}
        projectDetail={activeDetail}
        closeProjectDetail={closeMobileProjectDetail}
      />
    );
  }

  // ── Tablet environment (iPad-style) ──
  if (isTablet) {
    return (
      <TabletEnvironment
        coreApps={CORE_APPS}
        extraApps={EXTRA_APPS}
        appContent={APP_CONTENT}
        windows={windows}
        openWindow={openWindow}
        closeWindow={closeWindow}
        minimizeWindow={minimizeWindow}
        maximizeWindow={maximizeWindow}
        focusWindow={focusWindow}
        bg={bgNew}
        projectDetailWindows={projectDetailWindows}
        openProjectDetail={openProjectDetail}
        closeProjectDetail={closeProjectDetailWindow}
      />
    );
  }

  return (
    <div className="desktop-env in-plane" id="desktop" ref={desktopRef} style={{ backgroundImage: `url(${bgNew})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      {/* Custom cursor */}
      <div className={`custom-cursor${desktopVisible ? ' active' : ''}`} ref={cursorRef} />
      <div className={`custom-cursor-dot${desktopVisible ? ' active' : ''}`} ref={cursorDotRef} />

      {/* Menu Bar */}
      {!isMobile && (
        <div className={`desktop-menubar${desktopVisible ? ' visible' : ''}`}>
          <span className="menubar-left">Exploring Curiosity</span>
          <div className="menubar-right">
            <span className="menubar-status">
              <span className="menubar-dot" />
              Open to Work · New York
            </span>
          </div>
        </div>
      )}

      {/* Desktop area */}
      {!isMobile && (
        <div className="desktop-area">
          {/* BinocularEyes top-right */}
          <div className="desktop-binocular-eyes">
            <BinocularEyes />
          </div>

          {/* App Windows */}
          {ALL_APPS.map((app) => {
            const win = windows[app.id];
            if (!win?.isOpen) return null;
            return (
              <DesktopWindow
                key={app.id}
                id={app.id}
                title={app.title}
                defaultWidth={app.defaultWidth}
                defaultHeight={app.defaultHeight}
                isOpen={win.isOpen}
                isMinimized={win.isMinimized}
                isMaximized={win.isMaximized}
                zIndex={win.zIndex}
                onFocus={() => focusWindow(app.id)}
                onClose={() => closeWindow(app.id)}
                onMinimize={() => minimizeWindow(app.id)}
                onMaximize={() => maximizeWindow(app.id)}
                dockIconRef={dockRefs.current[app.id]}
                isTablet={isTablet}
              >
                {APP_CONTENT[app.id]}
              </DesktopWindow>
            );
          })}

          {/* Project Detail Popup Windows */}
          {Object.entries(projectDetailWindows).map(([winId, { projectId, title }]) => {
            const win = windows[winId];
            if (!win?.isOpen) return null;
            return (
              <DesktopWindow
                key={winId}
                id={winId}
                title={`${title} - Detail`}
                defaultWidth={820}
                defaultHeight={640}
                isOpen={win.isOpen}
                isMinimized={win.isMinimized}
                isMaximized={win.isMaximized}
                zIndex={win.zIndex}
                onFocus={() => focusWindow(winId)}
                onClose={() => closeProjectDetailWindow(winId)}
                onMinimize={() => minimizeWindow(winId)}
                onMaximize={() => maximizeWindow(winId)}
                isTablet={isTablet}
              >
                <ProjectDetailContent projectId={projectId} />
              </DesktopWindow>
            );
          })}
        </div>
      )}

      {/* ── Mobile grid ── */}
      {isMobile && (
        <div className="mobile-app-grid">
          <button className="mobile-app-card" onClick={scrollToHome}>
            <div className="mobile-app-icon" style={{ background: 'rgba(79,195,247,0.14)', borderColor: 'rgba(79,195,247,0.3)', color: '#4FC3F7' }}><GoHome /></div>
            <span className="mobile-app-name">Home</span>
          </button>
          {CORE_APPS.map((app) => (
            <button
              key={app.id}
              className="mobile-app-card"
              onClick={() => openMobileApp(app.id)}
            >
              <div className="mobile-app-icon" style={{ background: `${app.color}22`, borderColor: `${app.color}44`, color: app.color }}>{app.icon}</div>
              <span className="mobile-app-name">
                {app.id.charAt(0).toUpperCase() + app.id.slice(1)}
              </span>
            </button>
          ))}
          <button className="mobile-app-card mobile-more-btn" onClick={() => setShowExtras(!showExtras)}>
            <div className="mobile-app-icon">{showExtras ? '×' : '⋯'}</div>
            <span className="mobile-app-name">{showExtras ? 'Less' : 'More'}</span>
          </button>
          {showExtras && EXTRA_APPS.map((app) => (
            <button
              key={app.id}
              className="mobile-app-card"
              onClick={() => openMobileApp(app.id)}
            >
              <div className="mobile-app-icon" style={{ background: `${app.color}22`, borderColor: `${app.color}44`, color: app.color }}>{app.icon}</div>
              <span className="mobile-app-name">
                {app.id.charAt(0).toUpperCase() + app.id.slice(1)}
              </span>
            </button>
          ))}
        </div>
      )}
      {/* ── Dock ── */}
      {!isMobile && (
        <div className={`desktop-dock${desktopVisible ? ' visible' : ''}`}>
          <button
            className="dock-item"
            onClick={scrollToHome}
            onMouseEnter={(e) => onDockHover(e, true)}
            onMouseLeave={(e) => onDockHover(e, false)}
          >
            <div className="dock-icon dock-icon-home" style={{ background: 'rgba(79,195,247,0.14)', borderColor: 'rgba(79,195,247,0.3)', color: '#4FC3F7' }}><GoHome /></div>
            <span className="dock-label">Home</span>
            <span className="dock-indicator" />
          </button>
          <div className="dock-divider" />
          {DOCK_APPS.map((app) => (
            <button
              key={app.id}
              className="dock-item"
              ref={(el) => { dockRefs.current[app.id] = el; }}
              onClick={() => openWindow(app.id)}
              onMouseEnter={(e) => onDockHover(e, true)}
              onMouseLeave={(e) => onDockHover(e, false)}
            >
              <div className="dock-icon" style={{ background: `${app.color}22`, borderColor: `${app.color}44`, color: app.color }}>{app.icon}</div>
              <span className="dock-label">{app.id.charAt(0).toUpperCase() + app.id.slice(1)}</span>
              <span className={`dock-indicator${windows[app.id]?.isOpen ? ' active' : ''}`} />
            </button>
          ))}
          <div className="dock-divider" />
          <button
            className="dock-item"
            onClick={() => openWindow('resume')}
            onMouseEnter={(e) => onDockHover(e, true)}
            onMouseLeave={(e) => onDockHover(e, false)}
          >
            <div className="dock-icon" style={{ background: 'rgba(76,175,80,0.14)', borderColor: 'rgba(76,175,80,0.3)', color: '#4CAF50' }}><HiArrowDownTray /></div>
            <span className="dock-label">Resume</span>
            <span className="dock-indicator" />
          </button>
          <div className="dock-divider" />
          <button
            className="dock-item"
            onClick={toggleLock}
            onMouseEnter={(e) => onDockHover(e, true)}
            onMouseLeave={(e) => onDockHover(e, false)}
          >
            <div className={`dock-icon dock-icon-lock${isLocked ? ' locked' : ''}`} style={{ background: isLocked ? 'rgba(232,93,52,0.18)' : 'rgba(255,255,255,0.06)', borderColor: isLocked ? 'rgba(232,93,52,0.4)' : 'rgba(255,255,255,0.1)', color: isLocked ? '#E85D34' : 'var(--text-muted)' }}>{isLocked ? <RiLock2Line /> : <RiLockUnlockLine />}</div>
            <span className="dock-label">{isLocked ? 'Unlock' : 'Lock'}</span>
            <span className="dock-indicator" />
          </button>
        </div>
      )}

      {/* Blog Redirect Popup */}
      {blogRedirecting && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.85)', padding: '24px 40px', borderRadius: '20px',
          color: 'white', fontFamily: 'var(--font-mono)', fontSize: '14px', zIndex: 999999,
          border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
        }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#4FC3F7', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          Opening Blogs...
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
};

export default Desktop;
